import { Controller } from "@hotwired/stimulus";

// Suggestions for a city field, from /cities. Used by the registration form, by "add a
// profile", and by the homepage search box (#124) - which is why it is its own controller
// rather than half of signup_form_controller: the search box has no slug to preview and no
// account to create, and copying fifty lines of fetch/debounce over there is how the two
// start disagreeing about what a city is.
//
// Pure enhancement: it fills a <datalist>, so without JS the field is still a text input that
// submits its value.
export default class extends Controller {
  static targets = ["input", "suggestions", "status"];

  static values = { url: String };

  disconnect() {
    clearTimeout(this.debounce);
    this.requestController?.abort();
    this.#finish();
  }

  changed() {
    const query = this.inputTarget.value.trim();
    clearTimeout(this.debounce);

    if (query.length < 2) {
      this.requestController?.abort();
      this.suggestionsTarget.replaceChildren();
      this.#finish();
      return;
    }

    this.debounce = setTimeout(() => this.fetch(query), 200);
  }

  async fetch(query) {
    this.requestController?.abort();
    const controller = new AbortController();
    this.requestController = controller;
    this.#start();

    const url = `${this.urlValue}?q=${encodeURIComponent(query)}`;
    try {
      const response = await window.fetch(url, {
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });
      if (!response.ok || controller.signal.aborted) return;

      const cities = await response.json();
      // The answer to a query the field has already moved past is not an answer any more.
      if (controller.signal.aborted || query !== this.inputTarget.value.trim()) return;

      this.suggestionsTarget.replaceChildren(
        ...cities.map((city) => {
          const option = document.createElement("option");
          option.value = city.name;
          return option;
        })
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        console.warn("[city-autocomplete] could not load suggestions", error);
      }
      return;
    } finally {
      if (this.requestController === controller) this.#finish();
    }
  }

  #start() {
    this.inputTarget.setAttribute("aria-busy", "true");
    if (this.hasStatusTarget) this.statusTarget.classList.remove("hidden");
  }

  #finish() {
    if (this.hasInputTarget) this.inputTarget.removeAttribute("aria-busy");
    if (this.hasStatusTarget) this.statusTarget.classList.add("hidden");
  }
}
